
import { Nullable } from "@jamesgmarks/utilities";
import { Grid } from "@mui/material";
import { useState } from "react";
import { Form, Button } from "react-bootstrap";
import { REACT_APP_API_ROOT_URI } from "../../../App";
import { ICommissionRecord } from "../../../redux/features/commissions/interfaces";
import { apiFetch } from "../../../redux/utils";
import { useAppSelector } from "../../../redux/hooks";
import { HHMonthPicker } from "../../parts/HHMonthPicker";
import { CommissionsTable } from "./CommissionsTable";

/**
 * Displays a monthly commissions view for one or more sales reps with date range filtering.
 *
 * The user selects a "From" and "To" month then clicks "Load". All selected rep IDs are sent
 * to the backend in a **single request** via the `salesRepIds` query param; the server handles
 * per-rep fetching and merging, tagging each record with `salesRepName` when multiple reps are
 * involved. Rep selection itself lives in the parent {@link CommissionsTabView}.
 *
 * @param salesRepIds - Array of sales rep IDs to include in the monthly query.
 *   Passed down from {@link CommissionsTabView}; empty arrays are ignored (no fetch fires).
 */
export const CommissionsMonthly = ({ salesRepIds }: { salesRepIds: number[] }) => {
  const commissionSource = useAppSelector(state => state.commissions.commissionSource);

  const now = new Date();
  const [fromYear, setFromYear] = useState(now.getFullYear());
  const [fromMonth, setFromMonth] = useState(now.getMonth()); // 0-indexed
  const [toYear, setToYear] = useState(now.getFullYear());
  const [toMonth, setToMonth] = useState(now.getMonth()); // 0-indexed

  const [commissions, setCommissions] = useState<ICommissionRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const legacyInvoiceStates = [
    'Draft',
    'Sent',
    'Viewed',
    'Partial',
    'Disputed',
    'Paid',
    'Pending',
    'Outstanding',
    'Declined',
  ];
  const hydraInvoiceStates = ['sent', 'partial', 'disputed', 'resolved', 'overdue', 'viewed', 'paid'];
  const invoiceStates = JSON.stringify({
    legacyInvoiceStates,
    hydraInvoiceStates,
  });

  /**
   * Fires a single API request for all selected rep IDs over the configured date range and
   * stores the returned records in local state for display.
   *
   * All rep IDs are passed to the backend via the `salesRepIds` query param (comma-separated);
   * the server resolves per-rep data, tags each record with `salesRepName` when multiple reps
   * are involved, and returns a flat merged list in one response.
   *
   * Users without `READ_ALL_COMMISSIONS` are restricted to their own rep's data regardless
   * of the multi-select state.
   *
   * Month values from state are 0-indexed (JS `Date` convention) and are converted to 1-indexed
   * before being sent to the API.
   */
  const fetchCommissions = async () => {
    const repIds = salesRepIds.filter(id => id > 0);

    if (repIds.length === 0) return;

    setIsLoading(true);
    try {
      // The path param is required by the route but is superseded by salesRepIds when multiple reps are selected.
      const response = await apiFetch(
        `${REACT_APP_API_ROOT_URI}/commissions/sales_reps/${repIds[0]}/monthly`
        + `?invoiceStates=${encodeURIComponent(invoiceStates)}`
        + `&fromYear=${fromYear}&fromMonth=${fromMonth + 1}`
        + `&toYear=${toYear}&toMonth=${toMonth + 1}`
        + `&source=${commissionSource}`
        + `&salesRepIds=${repIds.join(',')}`,
      );
      const data = await response.json();
      setCommissions((data.data ?? []) as ICommissionRecord[]);
    } catch (err) {
      console.error('Failed to load commissions:', err);
      setCommissions([]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Form.Group>
        <Grid container mt={1} p={2} spacing={2} alignItems='center'>
          <Grid item xs='auto'>
            <HHMonthPicker
              label="From"
              onChange={
                (d: Nullable<Date>) => {
                  setFromYear(d ? d.getFullYear() : now.getFullYear());
                  setFromMonth(d ? d.getMonth() : now.getMonth());
                }
              }
              value={new Date(fromYear, fromMonth, 1)}
            />
          </Grid>
          <Grid item xs='auto'>
            <HHMonthPicker
              label="To"
              onChange={
                (d: Nullable<Date>) => {
                  setToYear(d ? d.getFullYear() : now.getFullYear());
                  setToMonth(d ? d.getMonth() : now.getMonth());
                }
              }
              value={new Date(toYear, toMonth, 1)}
            />
          </Grid>
          <Grid item xs>
            <Button onClick={() => fetchCommissions()} disabled={isLoading}>
              {isLoading ? 'Loading...' : 'Load'}
            </Button>
          </Grid>
        </Grid>
      </Form.Group>

      <CommissionsTable commissionsList={commissions} />
    </>
  );
};
